import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { TEXT_GRAY } from '@/constants/colors';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { useConfigValue } from '@/utils/useConfigValue';
import { gql } from '@apollo/client';
import { FC } from 'react';

import {
    HeadingCaptionLabel,
    LeftRailNoneLabel,
    LeftRailTextButton,
    LeftRailValueTypography,
} from './PatientLeftRail';

export const CareTeamClinicianFragment = gql`
    fragment CareTeamClinicianFragment on Clinician {
        id
        firstName
        lastName
        fullName
        email
        disciplines {
            id
            displayName
        }
        licenses
    }
`;

export interface ICareTeamClinician {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    email: string;
    disciplines: {
        id: string;
        displayName: string;
    }[];
    licenses: string[];
}
interface IPatientCareTeamInfo {
    id: string;
    careTeams: {
        id: string;
        clinicians: ICareTeamClinician[];
    }[];
    permissions: {
        editCareTeam: boolean;
    };
}

type CareTeamClinicianWithRole = ICareTeamClinician & {
    role?: string;
};

export const PatientCareTeamInfo = ({ patient }: { patient: IPatientCareTeamInfo }) => {
    const { openDialog } = useDialog();
    const applyRnCaseManagerLabel = useConfigValue('care_team_case_manager_label_for_rn');

    let careTeamClinicians: CareTeamClinicianWithRole[] = patient.careTeams.flatMap(
        (careTeam) => careTeam.clinicians,
    );
    if (applyRnCaseManagerLabel) {
        // If we are applying the Case Manager label to RNs, derive a role field and sort RNs to the top of the care team lists
        careTeamClinicians = careTeamClinicians
            .map((clinician) => ({
                ...clinician,
                role: clinician.licenses.includes('RN') ? 'Case Manager' : undefined,
            }))
            .sort((a, b) => {
                const aIsRn = a.licenses.includes('RN') ? 0 : 1;
                const bIsRn = b.licenses.includes('RN') ? 0 : 1;
                return aIsRn - bIsRn;
            });
    }

    return (
        <VerticalStack spacing={1}>
            <HorizontalStack justifyContent='space-between'>
                <HeadingCaptionLabel>Care Team</HeadingCaptionLabel>
                {patient.permissions.editCareTeam && (
                    <LeftRailTextButton
                        onClick={() =>
                            openDialog(DialogType.EditPatientCareTeam, { patientId: patient.id })
                        }
                    >
                        Edit
                    </LeftRailTextButton>
                )}
            </HorizontalStack>
            <VerticalStack spacing={2}>
                {careTeamClinicians.length > 0 ? (
                    careTeamClinicians.map((clinician) => (
                        <CareTeamClinician key={clinician.id} clinician={clinician} />
                    ))
                ) : (
                    <LeftRailNoneLabel />
                )}
            </VerticalStack>
        </VerticalStack>
    );
};

interface ICareTeamClinicianProps {
    clinician: CareTeamClinicianWithRole;
    displayEmail?: boolean;
}

const CareTeamClinician: FC<ICareTeamClinicianProps> = ({ clinician }) => {
    const licenses = clinician.licenses.length ? ` (${clinician.licenses.join(', ')})` : '';

    return (
        <VerticalStack spacing={0}>
            {clinician.role && <HeadingCaptionLabel>{clinician.role}</HeadingCaptionLabel>}

            <LeftRailValueTypography
                sx={{
                    color: TEXT_GRAY,
                    textDecoration: 'none',
                }}
            >
                {clinician.fullName + licenses}
            </LeftRailValueTypography>
        </VerticalStack>
    );
};
